import { getErrorMessage } from "../utils/errorMessage";
import React, { useEffect, useState, useCallback } from "react";
import { delegationsAPI } from "../api/client.js";
import { toast } from "react-toastify";
import ConfirmModal from "../components/ConfirmModal";
import { hasRole } from "../utils/accessControl.js";
import { mayPerform } from "../utils/userAccess.js";
import "./DelegationManagement.css";
import { TableSkeleton } from "../components/LoadingSkeleton";
import EmptyState from "../components/EmptyState";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";

// Maps the numeric workflow stage (0/1/2/3) stored in the DB
// to a human-readable label for the UI. Purely cosmetic —
// if removed, the `|| `Stage ${d.stage}`` fallbacks still work
// and just show "Stage 1/2/3".
const STAGE_LABELS = {
  0: "All Stages",
  1: "Stage 1 - Hiring Manager",
  2: "Stage 2 - BU Head",
  3: "Stage 3 - HR Review",
};

const STATUS_COLORS = {
  active: "#22c55e",
  scheduled: "#f59e0b",
  expired: "#6b7280",
  revoked: "#ef4444",
};

export default function DelegationManagement() {
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [scope, setScope] = useState("outgoing");
  const [eligibility, setEligibility] = useState(null);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = String(storedUser?.email || "").trim().toLowerCase();
  const userRole = storedUser?.role;
  // Superadmins can create/edit/revoke delegations on behalf of any user.
  const isSuperAdmin = hasRole(userRole, ["super_admin"]);
  // Employee-specific action gates from the Delegations page matrix.
  const canManageDelegation = mayPerform("delegation", "manage");
  const canEditDelegation = mayPerform("delegation", "view");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedDelegation, setSelectedDelegation] = useState(null);
  const [detailsActivity, setDetailsActivity] = useState([]);

  const [stages, setStages] = useState([]);

  // Create form
  const [createForm, setCreateForm] = useState({
    delegate_email: "",
    delegation_type: "general",
    stage: 0,
    start_date: "",
    end_date: "",
    reason: "",
    delegator_email: "",
  });
  const [delegateSearch, setDelegateSearch] = useState("");
  const [delegateResults, setDelegateResults] = useState([]);
  const [showDelegateDropdown, setShowDelegateDropdown] = useState(false);
  const [creating, setCreating] = useState(false);
  const [delegationToRevoke, setDelegationToRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // On-behalf-of delegator picker (superadmin only)
  const [delegatorSearch, setDelegatorSearch] = useState("");
  const [delegatorResults, setDelegatorResults] = useState([]);
  const [showDelegatorDropdown, setShowDelegatorDropdown] = useState(false);

  // Edit form
  const [editForm, setEditForm] = useState({
    reason: "",
    end_date: "",
    start_date: "",
    delegation_type: "",
    delegate_email: "",
  });
  const [editDelegateSearch, setEditDelegateSearch] = useState("");
  const [editDelegateResults, setEditDelegateResults] = useState([]);
  const [showEditDelegateDropdown, setShowEditDelegateDropdown] = useState(false);
  const [editing, setEditing] = useState(false);

  const fetchDelegations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await delegationsAPI.getAll({ tab: activeTab, scope });
      setDelegations(res.data?.delegations || []);
    } catch (err) {
      console.error("Failed to fetch delegations:", err);
      toast.error("Failed to load delegations");
    } finally {
      setLoading(false);
    }
  }, [activeTab, scope]);

  useEffect(() => {
    fetchDelegations();
  }, [fetchDelegations]);

  useEffect(() => {
    delegationsAPI.getStages().then((res) => {
      setStages(res.data?.stages || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    delegationsAPI
      .getEligibility()
      .then((res) => {
        if (cancelled) return;
        const elig = res.data?.eligibility || null;
        setEligibility(elig);
        if (elig && !elig.canCreate && elig.isDelegate?.length > 0) {
          setScope("incoming");
        } else if (elig && !elig.canUseDelegation) {
          setScope("outgoing");
        }
        // Default the create-stage to the user's first approver stage
        const approverFirst = elig?.isApprover?.[0];
        if (approverFirst) {
          setCreateForm((prev) => ({
            ...prev,
            stage: Number(approverFirst),
          }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = async () => {
    if (!createForm.delegate_email) {
      toast.error("Please select a delegate");
      return;
    }

    setCreating(true);
    try {
      // For superadmin creating on behalf, include delegator_email.
      // For normal users, set it to undefined so the backend uses self.
      const payload = { ...createForm };
      if (!isSuperAdmin || !createForm.delegator_email) {
        delete payload.delegator_email;
      }
      await delegationsAPI.create(payload);
      toast.success("Delegation created successfully");
      setShowCreateModal(false);
      setCreateForm({
        delegate_email: "",
        delegation_type: "general",
        stage: 0,
        start_date: "",
        end_date: "",
        reason: "",
        delegator_email: "",
      });
      setDelegateSearch("");
      setDelegatorSearch("");
      fetchDelegations();
    } catch (err) {
      const msg = getErrorMessage(err, "Failed to create delegation");
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = (id) => {
    setDelegationToRevoke(id);
  };

  const confirmRevoke = async () => {
    if (!delegationToRevoke || revoking) return;
    const id = delegationToRevoke;
    try {
      setRevoking(true);
      await delegationsAPI.revoke(id);
      toast.success("Delegation revoked");
      setDelegationToRevoke(null);
      fetchDelegations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to revoke delegation"));
    } finally {
      setRevoking(false);
    }
  };

  const handleViewDetails = async (delegation) => {
    setSelectedDelegation(delegation);
    setShowDetailsModal(true);

    try {
      const res = await delegationsAPI.getById(delegation.id);
      setSelectedDelegation(res.data?.delegation || delegation);
      setDetailsActivity(res.data?.activity || []);
    } catch (err) {
      console.error("Failed to load delegation details:", err);
    }
  };

  const handleEdit = (delegation) => {
    setSelectedDelegation(delegation);
    setEditForm({
      reason: delegation.reason || "",
      end_date: delegation.end_date ? delegation.end_date.split("T")[0] : "",
      start_date: delegation.start_date ? delegation.start_date.split("T")[0] : "",
      delegation_type: delegation.delegation_type || "general",
      delegate_email: delegation.delegate_email || "",
    });
    setEditDelegateSearch(
      delegation.delegate_name
        ? `${delegation.delegate_name} (${delegation.delegate_email})`
        : delegation.delegate_email || ""
    );
    setEditDelegateResults([]);
    setShowEditDelegateDropdown(false);
    setShowEditModal(true);
  };

  const handleEditDelegateSearch = async (value) => {
    setEditDelegateSearch(value);
    setEditForm((prev) => ({ ...prev, delegate_email: "" }));
    if (value.length < 2) {
      setEditDelegateResults([]);
      setShowEditDelegateDropdown(false);
      return;
    }

    try {
      const res = await delegationsAPI.searchUsers(value);
      setEditDelegateResults(res.data?.users || []);
      setShowEditDelegateDropdown(true);
    } catch {
      setEditDelegateResults([]);
    }
  };

  const selectEditDelegate = (user) => {
    setEditForm((prev) => ({
      ...prev,
      delegate_email: user.email,
    }));
    setEditDelegateSearch(user.name ? `${user.name} (${user.email})` : user.email);
    setShowEditDelegateDropdown(false);
  };

  const handleEditSave = async () => {
    if (editForm.delegation_type === "conditional" && (!editForm.start_date || !editForm.end_date)) {
      toast.error("Please select start and end dates for a conditional delegation");
      return;
    }
    setEditing(true);
    try {
      await delegationsAPI.update(selectedDelegation.id, editForm);
      toast.success("Delegation updated");
      setShowEditModal(false);
      fetchDelegations();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update delegation"));
    } finally {
      setEditing(false);
    }
  };

  const handleDelegateSearch = async (value) => {
    setDelegateSearch(value);
    if (value.length < 2) {
      setDelegateResults([]);
      setShowDelegateDropdown(false);
      return;
    }

    try {
      const res = await delegationsAPI.searchUsers(value);
      setDelegateResults(res.data?.users || []);
      setShowDelegateDropdown(true);
    } catch {
      setDelegateResults([]);
    }
  };

  const selectDelegate = (user) => {
    setCreateForm((prev) => ({
      ...prev,
      delegate_email: user.email,
    }));
    setDelegateSearch(user.name ? `${user.name} (${user.email})` : user.email);
    setShowDelegateDropdown(false);
  };

  const handleDelegatorSearch = async (value) => {
    setDelegatorSearch(value);
    setCreateForm((prev) => ({ ...prev, delegator_email: "" }));
    if (value.length < 2) {
      setDelegatorResults([]);
      setShowDelegatorDropdown(false);
      return;
    }

    try {
      const res = await delegationsAPI.searchUsers(value);
      setDelegatorResults(res.data?.users || []);
      setShowDelegatorDropdown(true);
    } catch {
      setDelegatorResults([]);
    }
  };

  const selectDelegator = (user) => {
    setCreateForm((prev) => ({
      ...prev,
      delegator_email: user.email,
    }));
    setDelegatorSearch(
      user.name ? `${user.name} (${user.email})` : user.email
    );
    setShowDelegatorDropdown(false);
  };

  // Only show the stages the current user is actually assigned to
  // as an approver, so they are not confused about what to select.
  // Admins (or users who can approve every stage) see the full list.
  const approverStages = eligibility?.isApprover || [];
  const isAdminApprover =
    eligibility?.canCreate &&
    [1, 2, 3].every((s) => approverStages.includes(s));

  const allowedStages = stages.filter((s) => {
    if (isAdminApprover) {
      return [0, 1, 2, 3].includes(s.value);
    }
    // Stage 0 ("All Stages") only if user owns all three stages
    if (s.value === 0) {
      return [1, 2, 3].every((st) => approverStages.includes(st));
    }
    return approverStages.includes(s.value);
  });

  // Fallback: if the user owns no assignable stage, fall back to all
  // stages so the dropdown is never empty.
  const stageOptions =
    allowedStages.length > 0 ? allowedStages : stages;

  const renderTable = () => {
    if (loading) {
      return (
        <div style={{ padding: 24 }}>
          <TableSkeleton rows={6} cols={5} />
        </div>
      );
    }

    if (delegations.length === 0) {
      const msg = scope === "incoming"
        ? "You have not received any delegations."
        : activeTab === "active"
        ? "No active delegations."
        : activeTab === "scheduled"
        ? "No scheduled delegations."
        : "No delegation history.";
      return (
        <EmptyState icon={<HandshakeOutlinedIcon style={{ fontSize: 48, color: "#94A3B8" }} />} title="Nothing here" message={msg} />
      );
    }

    return (
      <div className="del-table-wrap">
        <table className="del-table">
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Stage</th>
              <th scope="col">Delegated To</th>
              <th scope="col">Start Date</th>
              <th scope="col">End Date</th>
              <th scope="col">Status</th>
              <th scope="col">Created</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {delegations.map((d) => (
              <tr key={d.id}>
                <td>
                  <span className={`del-type-badge ${d.delegation_type}`}>
                    {d.delegation_type === "general" ? "General" : "Conditional"}
                  </span>
                </td>
                <td>{STAGE_LABELS[d.stage] || `Stage ${d.stage}`}</td>
                <td>
                  <div className="del-delegate-info">
                    <span className="del-delegate-name">{d.delegate_name || d.delegate_email}</span>
                    <span className="del-delegate-email">{d.delegate_email}</span>
                  </div>
                </td>
                <td>{d.start_date || "—"}</td>
                <td>
                  {d.delegation_type === "general" && d.status !== "expired" && d.status !== "revoked"
                    ? "No Expiry"
                    : d.end_date || "—"}
                </td>
                <td>
                  <span
                    className="del-status-badge"
                    style={{
                      backgroundColor: STATUS_COLORS[d.status] || "#6b7280",
                    }}
                  >
                    {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                  </span>
                </td>
                <td>{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                <td>
                  <div className="del-actions">
                    <button
                      className="del-action-btn view"
                      onClick={() => handleViewDetails(d)}
                      title="View Details"
                    >
                      View
                    </button>
                    {(d.status === "active" || d.status === "scheduled") &&
                      String(d?.delegate_email || "").trim().toLowerCase() !==
                        userEmail && (
                        <>
                          {canEditDelegation && (
                          <button
                            className="del-action-btn edit"
                            onClick={() => handleEdit(d)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          )}
                          {canManageDelegation && (
                          <button
                            className="del-action-btn revoke"
                            onClick={() => handleRevoke(d.id)}
                            title="Revoke"
                          >
                            Revoke
                          </button>
                          )}
                        </>
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="del-container">
      <div className="del-card">
        {/* Header */}
        <div className="del-header">
          <h2>Delegation Management</h2>
          {scope === "outgoing" && canEditDelegation && (
            <button
              className="del-create-btn"
              onClick={() => {
                setCreateForm({
                  delegate_email: "",
                  delegation_type: "general",
                  stage: Number(eligibility?.isApprover?.[0] ?? 0),
                  start_date: "",
                  end_date: "",
                  reason: "",
                  delegator_email: "",
                });
                setDelegateSearch("");
                setDelegatorSearch("");
                setShowCreateModal(true);
              }}
            >
              + New Delegation
            </button>
          )}
        </div>

        {/* Scope toggle */}
        <div className="del-scope-toggle">
          <button
            className={`del-tab ${scope === "outgoing" ? "active" : ""}`}
            onClick={() => setScope("outgoing")}
          >
            My Delegations
          </button>
          <button
            className={`del-tab ${scope === "incoming" ? "active" : ""}`}
            onClick={() => setScope("incoming")}
          >
            Delegated To Me
          </button>
        </div>

        {scope === "incoming" && (
          <div className="del-incoming-note">
            Showing delegations where you are the delegate (view only).
          </div>
        )}

        {/* Tabs */}
        <div className="del-tabs">
          {["active", "scheduled", "history"].map((tab) => (
            <button
              key={tab}
              className={`del-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "active"
                ? "Active Delegations"
                : tab === "scheduled"
                ? "Scheduled Delegations"
                : "Delegation History"}
            </button>
          ))}
        </div>

        {/* Table */}
        {renderTable()}
      </div>

      {/* ============ CREATE MODAL ============ */}
      {showCreateModal && (
        <div className="del-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="del-modal" onClick={(e) => e.stopPropagation()}>
            <div className="del-modal-header">
              <h3>New Delegation</h3>
              <button className="del-modal-close" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <div className="del-modal-body">
              {/* Delegate To */}
              <div className="del-form-group">
                <label>Delegate To *</label>
                <div className="del-search-wrap">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={delegateSearch}
                    onChange={(e) => handleDelegateSearch(e.target.value)}
                    onFocus={() => delegateResults.length > 0 && setShowDelegateDropdown(true)}
                  />
                  {showDelegateDropdown && delegateResults.length > 0 && (
                    <div className="del-search-dropdown">
                      {delegateResults.map((u) => (
                        <div
                          key={u.id}
                          className="del-search-option"
                          onClick={() => selectDelegate(u)}
                        >
                          <span className="del-option-name">{u.name}</span>
                          <span className="del-option-email">{u.email}</span>
                          <span className="del-option-role">{u.role}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* On Behalf Of (superadmin only) */}
              {isSuperAdmin && (
                <div className="del-form-group">
                  <label>On Behalf Of (Delegator)</label>
                  <div className="del-search-wrap">
                    <input
                      type="text"
                      placeholder="Leave blank to delegate as yourself"
                      value={delegatorSearch}
                      onChange={(e) => handleDelegatorSearch(e.target.value)}
                      onFocus={() =>
                        delegatorResults.length > 0 &&
                        setShowDelegatorDropdown(true)
                      }
                    />
                    {showDelegatorDropdown && delegatorResults.length > 0 && (
                      <div className="del-search-dropdown">
                        {delegatorResults.map((u) => (
                          <div
                            key={u.id}
                            className="del-search-option"
                            onClick={() => selectDelegator(u)}
                          >
                            <span className="del-option-name">{u.name}</span>
                            <span className="del-option-email">{u.email}</span>
                            <span className="del-option-role">{u.role}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delegation Type */}
              <div className="del-form-group">
                <label>Delegation Type *</label>
                <select
                  value={createForm.delegation_type}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      delegation_type: e.target.value,
                    }))
                  }
                >
                  <option value="general">General (No Expiry)</option>
                  <option value="conditional">Conditional (Date Range)</option>
                </select>
              </div>

              {/* Stage */}
              <div className="del-form-group">
                <label>Workflow Stage *</label>
                <select
                  value={createForm.stage}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      stage: Number(e.target.value),
                    }))
                  }
                >
                  {stageOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Conditional dates */}
              {createForm.delegation_type === "conditional" && (
                <div className="del-date-row">
                  <div className="del-form-group">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={createForm.start_date}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="del-form-group">
                    <label>End Date *</label>
                    <input
                      type="date"
                      value={createForm.end_date}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Reason */}
              <div className="del-form-group">
                <label>Reason</label>
                <textarea
                  rows={3}
                  placeholder="Optional reason for this delegation..."
                  value={createForm.reason}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="del-modal-footer">
              <button
                className="del-btn cancel"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button
                className="del-btn primary"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? "Creating..." : "Create Delegation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ EDIT MODAL ============ */}
      {showEditModal && selectedDelegation && (
        <div className="del-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="del-modal" onClick={(e) => e.stopPropagation()}>
            <div className="del-modal-header">
              <h3>Edit Delegation</h3>
              <button className="del-modal-close" onClick={() => setShowEditModal(false)}>
                ×
              </button>
            </div>
            <div className="del-modal-body">
              {/* Delegate To */}
              <div className="del-form-group">
                <label>Delegate To</label>
                <div className="del-search-wrap">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={editDelegateSearch}
                    onChange={(e) => handleEditDelegateSearch(e.target.value)}
                    onFocus={() =>
                      editDelegateResults.length > 0 &&
                      setShowEditDelegateDropdown(true)
                    }
                  />
                  {showEditDelegateDropdown && editDelegateResults.length > 0 && (
                    <div className="del-search-dropdown">
                      {editDelegateResults.map((u) => (
                        <div
                          key={u.id}
                          className="del-search-option"
                          onClick={() => selectEditDelegate(u)}
                        >
                          <span className="del-option-name">{u.name}</span>
                          <span className="del-option-email">{u.email}</span>
                          <span className="del-option-role">{u.role}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Delegation Type */}
              <div className="del-form-group">
                <label>Delegation Type</label>
                <select
                  value={editForm.delegation_type}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      delegation_type: e.target.value,
                    }))
                  }
                >
                  <option value="general">General (No Expiry)</option>
                  <option value="conditional">Conditional (Date Range)</option>
                </select>
              </div>

              {/* Conditional dates */}
              {editForm.delegation_type === "conditional" && (
                <div className="del-date-row">
                  <div className="del-form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={editForm.start_date}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          start_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="del-form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={editForm.end_date}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}
              <div className="del-form-group">
                <label>Reason</label>
                <textarea
                  rows={3}
                  value={editForm.reason}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="del-modal-footer">
              <button
                className="del-btn cancel"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="del-btn primary"
                onClick={handleEditSave}
                disabled={editing}
              >
                {editing ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ DETAILS MODAL ============ */}
      {showDetailsModal && selectedDelegation && (
        <div className="del-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="del-modal del-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="del-modal-header">
              <h3>Delegation Details</h3>
              <button className="del-modal-close" onClick={() => setShowDetailsModal(false)}>
                ×
              </button>
            </div>
            <div className="del-modal-body">
              <div className="del-detail-grid">
                <div className="del-detail-row">
                  <span className="del-detail-label">Delegated By</span>
                  <span className="del-detail-value">
                    {selectedDelegation.delegator_name || selectedDelegation.delegator_email}
                  </span>
                </div>
                <div className="del-detail-row">
                  <span className="del-detail-label">Delegated To</span>
                  <span className="del-detail-value">
                    {selectedDelegation.delegate_name || selectedDelegation.delegate_email}
                  </span>
                </div>
                <div className="del-detail-row">
                  <span className="del-detail-label">Type</span>
                  <span className="del-detail-value">
                    <span className={`del-type-badge ${selectedDelegation.delegation_type}`}>
                      {selectedDelegation.delegation_type === "general" ? "General" : "Conditional"}
                    </span>
                  </span>
                </div>
                <div className="del-detail-row">
                  <span className="del-detail-label">Stage</span>
                  <span className="del-detail-value">
                    {STAGE_LABELS[selectedDelegation.stage] || `Stage ${selectedDelegation.stage}`}
                  </span>
                </div>
                <div className="del-detail-row">
                  <span className="del-detail-label">Valid</span>
                  <span className="del-detail-value">
                    {selectedDelegation.delegation_type === "conditional"
                      ? `${selectedDelegation.start_date || "—"} → ${selectedDelegation.end_date || "—"}`
                      : "Ongoing (No Expiry)"}
                  </span>
                </div>
                <div className="del-detail-row">
                  <span className="del-detail-label">Status</span>
                  <span className="del-detail-value">
                    <span
                      className="del-status-badge"
                      style={{
                        backgroundColor:
                          STATUS_COLORS[selectedDelegation.status] || "#6b7280",
                      }}
                    >
                      {selectedDelegation.status?.charAt(0).toUpperCase() +
                        selectedDelegation.status?.slice(1)}
                    </span>
                  </span>
                </div>
                {selectedDelegation.reason && (
                  <div className="del-detail-row">
                    <span className="del-detail-label">Reason</span>
                    <span className="del-detail-value">{selectedDelegation.reason}</span>
                  </div>
                )}
                <div className="del-detail-row">
                  <span className="del-detail-label">Created</span>
                  <span className="del-detail-value">
                    {selectedDelegation.created_at
                      ? new Date(selectedDelegation.created_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
                {selectedDelegation.revoked_at && (
                  <div className="del-detail-row">
                    <span className="del-detail-label">Revoked</span>
                    <span className="del-detail-value">
                      {new Date(selectedDelegation.revoked_at).toLocaleString()}
                      {selectedDelegation.revoked_by
                        ? ` by ${selectedDelegation.revoked_by}`
                        : ""}
                    </span>
                  </div>
                )}
              </div>

              {/* Workflow Activity */}
              {detailsActivity.length > 0 && (
                <div className="del-activity-section">
                  <h4>Workflow Activity Under This Delegation</h4>
                  <table className="del-activity-table">
                    <thead>
                      <tr>
                        <th scope="col">Offer</th>
                        <th scope="col">Action</th>
                        <th scope="col">Handler</th>
                        <th scope="col">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsActivity.map((a) => (
                        <tr key={a.id}>
                          <td>{a.offer_id ? `#${a.offer_id}` : "—"}</td>
                          <td>{a.action_type}</td>
                          <td>{a.actual_handler}</td>
                          <td>
                            {a.created_at
                              ? new Date(a.created_at).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {detailsActivity.length === 0 && (
                <div className="del-activity-section">
                  <h4>Workflow Activity Under This Delegation</h4>
                  <p className="del-no-activity">No workflow activity recorded yet.</p>
                </div>
              )}
            </div>
            <div className="del-modal-footer">
              <button
                className="del-btn cancel"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    <ConfirmModal
      isOpen={!!delegationToRevoke}
      variant="warning"
      title="Revoke delegation?"
      message="Are you sure you want to revoke this delegation?"
      confirmLabel="Revoke"
      cancelLabel="Cancel"
      isLoading={revoking}
      loadingText="Revoking..."
      onConfirm={confirmRevoke}
      onCancel={() => setDelegationToRevoke(null)}
    />
    </div>
  );
}
